import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, IconButton, Chip,
  TextField, InputAdornment, Avatar, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, MenuItem, CircularProgress, Tooltip, Alert, Autocomplete
} from '@mui/material';
import { Add, Edit, Search, HowToReg } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { enrolmentsAPI, studentsAPI, settingsAPI, teachersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const EMPTY = { student_id: null, class_id: '', academic_year_id: '', enrolment_date: '', notes: '' };

export default function Enrolments() {
  const { enqueueSnackbar } = useSnackbar();
  const [enrolments, setEnrolments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const isClassTeacher = user?.role === 'class_teacher';
  const [classes, setClasses] = useState([]);
  const [homeClasses, setHomeClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [students, setStudents] = useState([]);
  const [filterYear, setFilterYear] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await enrolmentsAPI.getAll({
        search, page: page + 1, limit: rowsPerPage,
        academic_year_id: filterYear || undefined,
        class_id: filterClass || undefined
      });
      if (res.data.success) { setEnrolments(res.data.data); setTotal(res.data.total); }
    } finally { setLoading(false); }
  }, [search, page, rowsPerPage, filterYear, filterClass]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    if (user?.teacher_id) {
      teachersAPI.getMyHomeClasses().then(r => { if (r.data.success) setHomeClasses(r.data.data); }).catch(() => {});
    }
    settingsAPI.getAcademicYears().then(r => {
      if (r.data.success) {
        setAcademicYears(r.data.data);
        const current = r.data.data.find(y => y.is_current);
        if (current) { setFilterYear(current.id); setForm(f => ({ ...f, academic_year_id: current.id })); }
      }
    }).catch(() => {});
    studentsAPI.getAll({ limit: 1000 }).then(r => { if (r.data.success) setStudents(r.data.data); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (filterYear) {
      enrolmentsAPI.getStats({ academic_year_id: filterYear }).then(r => {
        if (r.data.success) setStats(r.data.data);
      }).catch(() => {});
    }
  }, [filterYear]);

  const openCreate = () => {
    setEditing(null);
    const current = academicYears.find(y => y.is_current);
    setForm({ ...EMPTY, academic_year_id: current?.id || '' });
    setDialogOpen(true);
  };

  const openEdit = (enrolment) => {
    setEditing(enrolment);
    setForm({
      student_id: enrolment.student_id,
      class_id: enrolment.class_id,
      academic_year_id: enrolment.academic_year_id,
      enrolment_date: enrolment.enrolment_date?.split('T')[0] || '',
      notes: enrolment.notes || '',
      status: enrolment.status || 'active'
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const submitForm = { ...form, academic_year_id: form.academic_year_id || filterYear };
    if (!submitForm.student_id || !submitForm.class_id || !submitForm.academic_year_id) {
      return enqueueSnackbar('Student, class and academic year are required', { variant: 'warning' });
    }
    setSaving(true);
    try {
      if (editing) await enrolmentsAPI.update(editing.id, submitForm);
      else await enrolmentsAPI.enrol(submitForm);
      enqueueSnackbar(editing ? 'Enrolment updated' : 'Student enrolled successfully', { variant: 'success' });
      setDialogOpen(false);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Error saving enrolment', { variant: 'error' });
    } finally { setSaving(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Enrolment Management</Typography>
        <Button variant="contained" startIcon={<HowToReg />} onClick={openCreate}>Enrol Student</Button>
      </Box>

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Total Enrolled', value: stats.summary?.total_enrolments },
            { label: 'Male', value: stats.summary?.male_count },
            { label: 'Female', value: stats.summary?.female_count },
            { label: 'Classes Active', value: stats.summary?.classes_count },
          ].map(s => (
            <Grid item xs={6} sm={3} key={s.label}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="h5" fontWeight={700} color="primary.main">{s.value ?? '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField placeholder="Search students..." value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select label="Academic Year" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                <MenuItem value="">All Years</MenuItem>
                {academicYears.map(y => (
                  <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' (Current)' : ''}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select label="Class" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
                <MenuItem value="">All Classes</MenuItem>
                {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Student</TableCell>
                <TableCell>Admission No.</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Academic Year</TableCell>
                <TableCell>Enrolment Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow>
                : enrolments.length === 0
                ? <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>No enrolments found</TableCell></TableRow>
                : enrolments.map(e => (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: 12 }}>
                          {e.first_name?.[0]}{e.last_name?.[0]}
                        </Avatar>
                        <Typography fontSize={14} fontWeight={600}>{e.first_name} {e.last_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Chip label={e.admission_number} size="small" variant="outlined" /></TableCell>
                    <TableCell>{e.class_name}{e.stream ? ` (${e.stream})` : ''}</TableCell>
                    <TableCell>{e.year_label} — {e.term}</TableCell>
                    <TableCell>{e.enrolment_date ? new Date(e.enrolment_date).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>
                      <Chip label={e.status} size="small" color={e.status === 'active' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(e)}><Edit fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]} />
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Enrolment' : 'Enrol Student'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {!editing && (
              <Grid item xs={12}>
                <Autocomplete
                  options={students}
                  getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                  onChange={(_, val) => setForm({ ...form, student_id: val?.id || null })}
                  renderInput={params => <TextField {...params} label="Select Student *" />}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField select label="Class *" value={form.class_id}
                onChange={e => setForm({ ...form, class_id: e.target.value })}>
                {(isClassTeacher ? homeClasses : classes).map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Enrolment Date" type="date" value={form.enrolment_date}
                onChange={e => setForm({ ...form, enrolment_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            {editing && (
              <Grid item xs={12} sm={6}>
                <TextField select label="Status" value={form.status || 'active'}
                  onChange={e => setForm({ ...form, status: e.target.value })}>
                  {['active','withdrawn','completed','transferred'].map(s =>
                    <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Update' : 'Enrol'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
